<script setup lang="ts">
/**
 * Footer V2 — Logo + Redes sociais.
 *
 * Container: padding 12 16, space-between, bg branco 10%, radius 9.
 *
 * Fontes:
 *   - Logo: asset estático local /assets/images/logo.avif
 *           (mesma fonte do BaseCommonLogo; aqui usamos <img> direto
 *            pois o BaseCommonLogo tem tamanhos fixos próprios).
 *   - Redes sociais: useAppearanceStore().appearance.social (API /appearance,
 *           normalizado via normalizeStringJsonObject — mesma lógica do
 *           components/footer/Social.vue legado).
 *
 * Ícones renderizados via NuxtIcon (SVGs em assets/icons/social/*.svg
 * usam fill="currentColor", então color: #FFF basta).
 */

const { t } = useTexts();
const appearance = useAppearanceStore();

type SocialItem = {
  name: string;
  icon: string;
  link: string;
  title: string;
  width: number;
  height: number;
};

// Dimensões por rede (em px). Redes ausentes do mapa caem no DEFAULT_SIZE.
const DEFAULT_SIZE = { width: 16, height: 16 };
const SOCIAL_SIZES: Record<string, { width: number; height: number }> = {
  instagram: { width: 16.009, height: 15.755 },
  youtube: { width: 16, height: 12 },
  tiktok: { width: 13, height: 15 },
  whatsapp: { width: 15, height: 15 },
};

const social = computed(() => {
  return normalizeStringJsonObject(appearance.appearance.social);
});

function buildItem(
  name: string,
  icon: string,
  link: string,
  title: string,
): SocialItem {
  const size = SOCIAL_SIZES[name] ?? DEFAULT_SIZE;

  return { name, icon, link, title, width: size.width, height: size.height };
}

const socialItems = computed<SocialItem[]>(() => {
  if (!social.value) return [];

  const items: SocialItem[] = [];

  if (social.value.instagram)
    items.push(
      buildItem(
        'instagram',
        'social/instagram',
        social.value.instagram,
        t('footer.instagram'),
      ),
    );
  if (social.value.facebook)
    items.push(
      buildItem(
        'facebook',
        'social/facebook',
        social.value.facebook,
        t('footer.facebook'),
      ),
    );
  if (social.value.twitter)
    items.push(
      buildItem('X', 'social/x', social.value.twitter, t('footer.twitter')),
    );
  if (social.value.youtube)
    items.push(
      buildItem(
        'youtube',
        'social/youtube',
        social.value.youtube,
        t('footer.youtube'),
      ),
    );
  if (social.value.tiktok)
    items.push(
      buildItem(
        'tiktok',
        'social/tiktok',
        social.value.tiktok,
        t('footer.tiktok'),
      ),
    );
  if (social.value.whatsapp)
    items.push(
      buildItem(
        'whatsapp',
        'social/whatsapp',
        social.value.whatsapp,
        t('footer.whatsapp'),
      ),
    );
  if (social.value.telegram)
    items.push(
      buildItem(
        'telegram',
        'social/telegram',
        social.value.telegram,
        t('footer.telegram'),
      ),
    );

  return items;
});

const hasAnySocial = computed(() => socialItems.value.length > 0);

const logoUrl = computed(() =>
  getAssetsUrlForCDN('/assets/images/aposta-tudo.png'),
);

// Compilado uma única vez no carregamento do módulo (era recriado a cada
// chamada dentro do v-for, multiplicando alocações por nº de redes sociais).
const PROTOCOL_REGEX = /^https?:\/\//i;

function linkFilter(link: string) {
  return PROTOCOL_REGEX.test(link) ? link : `https://${link}`;
}
</script>

<template>
  <div class="footer-v2-logo-social">
    <NuxtLink
      :aria-label="appName()"
      class="footer-v2-logo-social__logo-link"
      no-prefetch
      :to="localeLink('/')"
    >
      <img
        :alt="appName()"
        class="footer-v2-logo-social__logo"
        decoding="async"
        height="15"
        loading="lazy"
        :src="logoUrl"
        width="120"
      />
    </NuxtLink>

    <div
      v-if="hasAnySocial"
      class="footer-v2-logo-social__icons"
    >
      <a
        v-for="item in socialItems"
        :key="item.name"
        :aria-label="item.title"
        class="footer-v2-logo-social__icon-link"
        :href="linkFilter(item.link)"
        rel="noopener noreferrer"
        :style="{
          /* CSS vars permitem override por media query (inline style
             ganharia se aplicássemos width/height direto). */
          '--icon-w': `${item.width}px`,
          '--icon-h': `${item.height}px`,
        }"
        target="_blank"
        :title="item.title"
      >
        <NuxtIcon
          aria-hidden="true"
          class="footer-v2-logo-social__icon"
          :name="item.icon"
        />
        <span class="sr-only">{{ item.title }}</span>
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-logo-social {
  display: flex;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.1);

  /* Desktop (≥1024px) — Coluna esquerda do footer:
       container flat (sem bg/padding/radius), 281px de largura,
       flex-col com logo + linha de ícones empilhados. */
  @media (min-width: 1024px) {
    width: 281px;
    padding: 0;
    background: transparent;
    border-radius: 0;
    flex-direction: column;
    align-items: flex-start;
    align-self: auto;
    gap: 18px;
  }

  &__logo-link {
    display: inline-flex;
    align-items: center;
  }

  &__logo {
    display: block;
    /* Mobile: 119.696 x 14.962 */
    width: 119.696px;
    height: 14.962px;
    object-fit: contain;

    /* Desktop: 210 x 26.25 (aspect 8/1) */
    @media (min-width: 1024px) {
      width: 209.999px;
      height: 26.25px;
    }
  }

  &__icons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }

  &__icon-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--icon-w);
    height: var(--icon-h);
    color: #fff;
    text-decoration: none;

    /* Desktop: bolha 34x34 com bg branco 10% e ícone interno menor.
       Circle r=17, fill rgba(255,255,255,0.10).
       O ícone interno (path) ocupa ~16px centralizado. */
    @media (min-width: 1024px) {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
    }
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 0;

    /* Os SVGs em assets/icons/social têm width/height="1em" inline.
       Precisamos sobrescrever pra que o ícone preencha o <a> wrapper.
       !important é necessário pois atributos inline do SVG têm alta
       especificidade. */
    :deep(svg) {
      width: 100% !important;
      height: 100% !important;
      fill: currentColor;
      display: block;
    }

    /* Desktop: ícone interno menor que a bolha (16px num círculo de 34px). */
    @media (min-width: 1024px) {
      width: 16px;
      height: 16px;

      :deep(svg) {
        width: 16px !important;
        height: 16px !important;
      }
    }
  }

  /* Desktop: row de ícones alinhada à esquerda (não centralizada). */
  @media (min-width: 1024px) {
    &__icons {
      justify-content: flex-start;
      align-self: stretch;
    }
  }
}
</style>
